import { Box, Switch, TextField, Typography, Button, Chip, FormControl, InputLabel, Select, MenuItem, Divider } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import EventAvailableIcon from "@mui/icons-material/EventAvailable";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";

const glassCard = { p: 2, borderRadius: 3, bgcolor: "rgba(255,255,255,0.7)", backdropFilter: "blur(8px)", border: "1px solid rgba(229,231,235,0.6)", boxShadow: "0 2px 12px rgba(0,0,0,0.04)" };
const fieldSx = { "& .MuiOutlinedInput-root": { borderRadius: 2, bgcolor: "#fff", fontSize: 12 } };
const accentText = (a) => ({ ...fieldSx, "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: a }, "& .MuiInputLabel-root.Mui-focused": { color: a } });

const DAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

const generateSlots = (startHour, endHour, duration) => {
  const slots = [];
  let [sh, sm] = startHour.split(":").map(Number);
  let [eh, em] = endHour.split(":").map(Number);
  let cur = sh * 60 + sm;
  const end = eh * 60 + em;
  while (cur + duration <= end) {
    const h = Math.floor(cur / 60);
    const m = cur % 60;
    slots.push(`${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`);
    cur += duration;
  }
  return slots;
};

const AppointmentBookingTab = ({ skipOption, setSkipOption, errorMessage, setErrorMessage, settings = {}, setSettings, accentColor = "#10b981" }) => {
  const startHour = settings.startHour || "09:00";
  const endHour = settings.endHour || "17:00";
  const duration = Number(settings.duration) || 30;
  const workingDays = settings.workingDays || [1, 2, 3, 4, 5];
  const minDate = settings.minDate || "";
  const maxDate = settings.maxDate || "";
  const confirmationMessage = settings.confirmationMessage || "Your appointment is confirmed!";
  const timezone = settings.timezone || "";

  const update = (patch) => setSettings({ ...settings, ...patch });

  const toggleDay = (d) => {
    const next = workingDays.includes(d) ? workingDays.filter((x) => x !== d) : [...workingDays, d].sort();
    update({ workingDays: next });
  };

  const sampleSlots = generateSlots(startHour, endHour, duration);

  return (
    <Box sx={{ mt: 1, display: "flex", flexDirection: "column", gap: 2 }}>
      <Box sx={glassCard}>
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <Box>
            <Typography sx={{ fontWeight: 600, fontSize: 13, color: "#1f2937" }}>Skip Option</Typography>
            <Typography sx={{ fontSize: 11, color: "#6b7280" }}>Allow visitors to skip booking</Typography>
          </Box>
          <Switch checked={!!skipOption} onChange={(e) => setSkipOption(e.target.checked)} size="small" />
        </Box>
      </Box>

      <Box sx={glassCard}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.5 }}>
          <AccessTimeIcon sx={{ fontSize: 16, color: accentColor }} />
          <Typography sx={{ fontWeight: 600, fontSize: 13, color: "#1f2937" }}>Working Hours</Typography>
        </Box>
        <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 1, mb: 1.5 }}>
          <TextField fullWidth size="small" type="time" label="Start"
            value={startHour}
            onChange={(e) => update({ startHour: e.target.value })}
            InputLabelProps={{ shrink: true }}
            sx={accentText(accentColor)} />
          <TextField fullWidth size="small" type="time" label="End"
            value={endHour}
            onChange={(e) => update({ endHour: e.target.value })}
            InputLabelProps={{ shrink: true }}
            sx={accentText(accentColor)} />
          <FormControl size="small" fullWidth>
            <InputLabel sx={{ "&.Mui-focused": { color: accentColor } }}>Duration</InputLabel>
            <Select value={duration} label="Duration"
              onChange={(e) => update({ duration: e.target.value })}
              sx={{ borderRadius: 2, bgcolor: "#fff", fontSize: 12, "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: accentColor } }}
            >
              {[15, 30, 45, 60, 90, 120].map((d) => (
                <MenuItem key={d} value={d} sx={{ fontSize: 12 }}>{d} min</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>

        <Typography sx={{ fontSize: 11, fontWeight: 600, color: "#374151", mb: 0.6 }}>Working days</Typography>
        <Box sx={{ display: "flex", gap: 0.6, flexWrap: "wrap" }}>
          {DAYS.map((d, i) => (
            <Chip key={i} label={d} size="small" onClick={() => toggleDay(i)}
              sx={{ fontWeight: 700, fontSize: 11, bgcolor: workingDays.includes(i) ? `${accentColor}18` : "#f3f4f6", color: workingDays.includes(i) ? accentColor : "#9ca3af", border: `1px solid ${workingDays.includes(i) ? accentColor : "transparent"}`, "&:hover": { bgcolor: workingDays.includes(i) ? `${accentColor}25` : "#e5e7eb" } }}
            />
          ))}
        </Box>
      </Box>

      <Box sx={glassCard}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.5 }}>
          <CalendarMonthIcon sx={{ fontSize: 16, color: accentColor }} />
          <Typography sx={{ fontWeight: 600, fontSize: 13, color: "#1f2937" }}>Date Range</Typography>
        </Box>
        <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 1 }}>
          <TextField fullWidth size="small" type="date" label="From"
            value={minDate} onChange={(e) => update({ minDate: e.target.value })}
            InputLabelProps={{ shrink: true }} sx={accentText(accentColor)} />
          <TextField fullWidth size="small" type="date" label="To"
            value={maxDate} onChange={(e) => update({ maxDate: e.target.value })}
            InputLabelProps={{ shrink: true }} sx={accentText(accentColor)} />
        </Box>
        <Typography sx={{ fontSize: 10, color: "#6b7280", mt: 0.8 }}>Leave empty to allow any future date.</Typography>
      </Box>

      <Box sx={glassCard}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.5 }}>
          <EventAvailableIcon sx={{ fontSize: 16, color: accentColor }} />
          <Typography sx={{ fontWeight: 600, fontSize: 13, color: "#1f2937" }}>Preview Slots</Typography>
        </Box>
        <Typography sx={{ fontSize: 11, color: "#6b7280", mb: 1 }}>
          Sample of slots generated for each working day:
        </Typography>
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
          {sampleSlots.length > 0 ? sampleSlots.map((s) => (
            <Box key={s} sx={{ px: 1, py: 0.4, bgcolor: `${accentColor}10`, border: `1px solid ${accentColor}30`, borderRadius: 1, fontSize: 10, fontWeight: 700, color: accentColor }}>{s}</Box>
          )) : <Typography sx={{ fontSize: 11, color: "#dc2626" }}>No slots — adjust working hours</Typography>}
        </Box>
      </Box>

      <Box sx={glassCard}>
        <Typography sx={{ fontWeight: 600, fontSize: 13, mb: 1, color: "#1f2937" }}>Confirmation Message</Typography>
        <TextField fullWidth placeholder="Your appointment is confirmed!" value={confirmationMessage} size="small"
          onChange={(e) => update({ confirmationMessage: e.target.value })}
          sx={accentText(accentColor)} />
        <Typography sx={{ fontSize: 10, color: "#6b7280", mt: 0.8 }}>Sent to visitor after they confirm their slot.</Typography>
      </Box>

      <Box sx={glassCard}>
        <Typography sx={{ fontWeight: 600, fontSize: 13, mb: 1, color: "#1f2937" }}>Error Message</Typography>
        <TextField fullWidth placeholder="Please pick a valid date" value={errorMessage || ""} size="small"
          onChange={(e) => setErrorMessage(e.target.value)}
          sx={fieldSx} />
      </Box>
    </Box>
  );
};

export default AppointmentBookingTab;
